import {BaseComponent} from '../base/component.base.js';
import template from './one-off-incomes.template.html';
import styles from './one-off-incomes.styles.css';
import {formatDate, formatEuro, moneyInputValue, validateOneOffIncomeForm} from '../../core/billing/billing.core.js';
import {escapeHtml} from '../../core/escape-html.js';
import {confirmDiscard} from '../confirm-dialog/confirm-discard.js';

/** Form control selector by field name; the names are the ones `validateOneOffIncomeForm` reads. */
const FIELDS = {
    label: '#one-off-label',
    invoiceNumber: '#one-off-invoice-number',
    invoicedOn: '#one-off-invoiced-on',
    amount: '#one-off-amount',
    cost: '#one-off-cost',
};

/**
 * The one-off invoices of a client: income invoiced once, shown apart from the recurring billing card, with no
 * status, renewal or invoice action. It owns the list and the add/edit form and only emits events; the driving
 * adapter talks to the API and hands the list in through `incomes`.
 *
 * Events (bubbling, composed): `one-off-save {slug, id | null, request}` and `one-off-delete {id, label}`
 * (after the user confirmed).
 */
export class OneOffIncomesComponent extends BaseComponent {
    /** Slug of the client shown, sent with `one-off-save`. */
    slug = '';
    /** @type {import('../../shell/types/billing.types.js').OneOffIncomeApiResponse[]} */
    #incomes = [];
    /** The income open in the form (`income` is null for a new one); null while the form is closed. */
    #editing = null;
    /** Form values as last opened; see `hasUnsavedChanges`. */
    #savedSnapshot = null;

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#one-off-form').addEventListener('submit', (e) => this.#handleSubmit(e));
        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));
        this.incomes = [];
    }

    /**
     * Shows the incomes. An income open in the form that is gone from `incomes` (deleted) closes the form.
     *
     * @param {import('../../shell/types/billing.types.js').OneOffIncomeApiResponse[]} incomes newest first
     */
    set incomes(incomes) {
        this.#incomes = incomes;
        this.$('#one-off-load-error').hidden = true;
        const editing = this.#editing?.income;
        if (editing && !incomes.some((income) => income.id === editing.id)) {
            this.closeForm();
        }
        this.#render();
    }

    /** @param {string} message why the incomes could not be loaded; shown instead of the list */
    showLoadError(message) {
        this.#incomes = [];
        this.$('#one-off-list').innerHTML = '';
        const error = this.$('#one-off-load-error');
        error.textContent = message;
        error.hidden = false;
    }

    /**
     * Shows why the backend rejected the income, in the form where it gets fixed.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} true when the form is open and shows the message
     */
    showFormError(message) {
        const error = this.$('#one-off-form-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.#editing !== null;
    }

    /** Closes the form and forgets what was typed in it. */
    closeForm() {
        this.#editing = null;
        this.#savedSnapshot = null;
        this.$('#one-off-form').hidden = true;
        this.showFormError(null);
    }

    /** @returns {boolean} true when the open form differs from the income as last opened */
    hasUnsavedChanges() {
        return this.#editing !== null && this.#snapshot() !== this.#savedSnapshot;
    }

    /**
     * Asks whether to drop the form's unsaved changes; the form itself is left as it is.
     *
     * @returns {Promise<boolean>} true when the user chose to discard
     */
    confirmDiscard() {
        const label = this.#editing.income?.label;
        return confirmDiscard(
            label ? `Discard changes to "${label}"?` : 'Discard the new one-off invoice?',
            label
                ? 'The changes you made to this one-off invoice are not saved and will be lost.'
                : 'The one-off invoice you started is not saved and will be lost.',
        );
    }

    /**
     * Forgets the incomes and the open form, for when another client is shown.
     *
     * @param {string} slug slug of the client that is shown next
     */
    reset(slug) {
        this.slug = slug;
        this.closeForm();
        this.incomes = [];
    }

    /** @param {boolean} value disables the save button while a request runs, so it cannot be sent twice */
    set submitting(value) {
        this.$('#btn-submit-one-off').disabled = value;
    }

    #render() {
        const list = this.$('#one-off-list');
        if (this.#incomes.length === 0) {
            list.innerHTML = '<p class="one-off-empty">No one-off invoices yet.</p>';
            return;
        }
        list.innerHTML = `<div class="table-wrap"><table class="one-off-table">
            <thead><tr><th scope="col">Date</th><th scope="col">Invoice</th><th scope="col">Label</th>
                <th scope="col" class="num">Amount</th><th scope="col" class="num">Cost</th><th scope="col" class="actions"><span class="visually-hidden">Actions</span></th></tr></thead>
            <tbody>${this.#incomes.map((income) => {
                const label = escapeHtml(income.label);
                return `<tr>
                <td class="nowrap">${escapeHtml(formatDate(income.invoicedOn))}</td>
                <td class="nowrap">${escapeHtml(income.invoiceNumber)}</td>
                <td>${label}<span class="one-off-tag">One-off</span></td>
                <td class="num">${escapeHtml(formatEuro(income.amount))}</td>
                ${income.cost === null ? '<td class="num" title="No supplier cost recorded">—</td>' : `<td class="num">${escapeHtml(formatEuro(income.cost))}</td>`}
                <td class="actions">
                    <button type="button" class="btn btn-outline btn-sm btn-one-off-edit" data-id="${income.id}" aria-label="Edit one-off invoice ${label}">
                        <i aria-hidden="true" class="fa-solid fa-pen-to-square"></i> Edit
                    </button>
                    <button type="button" class="btn btn-delete btn-sm btn-one-off-delete" data-id="${income.id}" aria-label="Delete one-off invoice ${label}">
                        <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                    </button>
                </td>
            </tr>`;
            }).join('')}</tbody></table></div>`;
    }

    /** @param {import('../../shell/types/billing.types.js').OneOffIncomeApiResponse | null} income null opens the form for a new one */
    #open(income) {
        const form = this.$('#one-off-form');
        this.#editing = {income};
        form.querySelector(FIELDS.label).value = income?.label ?? '';
        form.querySelector(FIELDS.invoiceNumber).value = income?.invoiceNumber ?? '';
        form.querySelector(FIELDS.invoicedOn).value = income?.invoicedOn ?? '';
        form.querySelector(FIELDS.amount).value = moneyInputValue(income?.amount);
        form.querySelector(FIELDS.cost).value = moneyInputValue(income?.cost);
        this.#showErrors({});
        this.showFormError(null);
        this.$('#one-off-form-title').textContent = income ? `Edit one-off invoice: ${income.label}` : 'Add one-off invoice';
        form.hidden = false;
        this.#savedSnapshot = this.#snapshot();
        this.$(FIELDS.label).focus();
    }

    #values() {
        return Object.fromEntries(Object.entries(FIELDS).map(([name, selector]) => [name, this.$(selector).value]));
    }

    #snapshot() {
        return JSON.stringify(this.#values());
    }

    /** Runs `action` when the form has no unsaved changes or the user chose to drop them. */
    async #whenFormMayBeDropped(action) {
        if (!this.hasUnsavedChanges() || await this.confirmDiscard()) {
            action();
        }
    }

    /** Shows one message under each field named in `errors`, clears the others and focuses the first one. */
    #showErrors(errors) {
        for (const [name, selector] of Object.entries(FIELDS)) {
            const message = errors[name] ?? '';
            const error = this.$(`#one-off-error-${name}`);
            error.textContent = message;
            error.hidden = !message;
            this.$(selector).setAttribute('aria-invalid', String(Boolean(message)));
        }
        const first = Object.keys(FIELDS).find((name) => errors[name]);
        if (first) {
            this.$(FIELDS[first]).focus();
        }
    }

    #find(button) {
        return this.#incomes.find((income) => income.id === Number(button.dataset.id));
    }

    async #handleClick(e) {
        if (e.target.closest('#btn-add-one-off')) {
            await this.#whenFormMayBeDropped(() => this.#open(null));
        }
        const btnEdit = e.target.closest('.btn-one-off-edit');
        if (btnEdit) {
            const income = this.#find(btnEdit);
            await this.#whenFormMayBeDropped(() => this.#open(income));
        }
        if (e.target.closest('#btn-cancel-one-off')) {
            await this.#whenFormMayBeDropped(() => this.closeForm());
        }
        const btnDelete = e.target.closest('.btn-one-off-delete');
        if (btnDelete) {
            const income = this.#find(btnDelete);
            const confirmed = await document.querySelector('admin-confirm-dialog').ask({
                title: `Delete one-off invoice "${income.label}"?`,
                body: 'The invoice is removed and its amount no longer counts as income in the results.',
                confirmLabel: 'Delete invoice',
                cancelLabel: 'Cancel',
                danger: true,
            });
            if (confirmed) {
                this.emit('one-off-delete', {id: income.id, label: income.label});
            }
        }
    }

    #handleSubmit(e) {
        e.preventDefault();
        this.showFormError(null);
        const {valid, errors, request} = validateOneOffIncomeForm(this.#values());
        this.#showErrors(errors);
        if (valid) {
            this.emit('one-off-save', {slug: this.slug, id: this.#editing.income?.id ?? null, request});
        }
    }
}

customElements.define('one-off-incomes', OneOffIncomesComponent);
